var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import styled from "styled-components";
import Text from "../Text/Text";
import IconButton from "../IconButton/IconButton";
import { IconX } from "@tabler/icons-react";
import { useTheme } from "../ThemeProvider/ThemeProvider";
import { useDebounce } from "../../helpers/CustomHooks";
var getSize = function (size) {
    switch (size) {
        case "small":
            return 320;
        case "medium":
            return 480;
        case "large":
            return 640;
        default:
            return 480;
    }
};
var getBackdropStrength = function (strength) {
    switch (strength) {
        case "light":
            return "rgba(0, 0, 0, 0.1)";
        case "medium":
            return "rgba(0, 0, 0, 0.3)";
        case "strong":
            return "rgba(0, 0, 0, 0.5)";
        default:
            return "rgba(0, 0, 0, 0.3)";
    }
};
var StyledDrawer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  width: ", ";\n  position: fixed;\n  top: 0;\n  ", ": ", ";\n  height: 100%;\n  background-color: ", ";\n  color: ", ";\n  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);\n  transition: ", "  \n  z-index: ", ";\n"], ["\n  width: ", ";\n  position: fixed;\n  top: 0;\n  ", ": ", ";\n  height: 100%;\n  background-color: ", ";\n  color: ", ";\n  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);\n  transition: ", "  \n  z-index: ", ";\n"])), function (props) { return "".concat(getSize(props.$size), "px"); }, function (props) { return props.$position; }, function (props) {
    return props.$open ? 0 : "".concat(-1 * getSize(props.$size), "px");
}, function (props) { return props.$colorPalette.primary.background; }, function (props) { return props.$colorPalette.primary.grayScale[11]; }, function (props) { return "".concat(props.$position, " 0.3s ease-in-out;"); }, function (props) { return props.$zIndex; });
var StyledBackdrop = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: fixed;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 100%;\n  z-index: 999;\n  background-color: ", ";\n"], ["\n  position: fixed;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 100%;\n  z-index: 999;\n  background-color: ", ";\n"])), function (props) { return getBackdropStrength(props.$backdropStrength); });
var StyledHeader = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  padding: 1rem;\n  border-bottom: ", ";\n"], ["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  padding: 1rem;\n  border-bottom: ", ";\n"])), function (props) {
    return "1px solid ".concat(props.$colorPalette.primary.grayScale[5]);
});
var Drawer = function (_a) {
    var children = _a.children, _b = _a.open, open = _b === void 0 ? false : _b, _c = _a.position, position = _c === void 0 ? "left" : _c, _d = _a.size, size = _d === void 0 ? "medium" : _d, className = _a.className, style = _a.style, _e = _a.hideOnOutsideClick, hideOnOutsideClick = _e === void 0 ? true : _e, _f = _a.zIndex, zIndex = _f === void 0 ? 1000 : _f, _g = _a.backdropStrength, backdropStrength = _g === void 0 ? "medium" : _g, title = _a.title, _h = _a.hasCloseIcon, hasCloseIcon = _h === void 0 ? true : _h, 
    // Events
    onClose = _a.onClose;
    var _j = React.useState(open), isOpen = _j[0], setIsOpen = _j[1];
    var debouncedOpen = useDebounce(open, 300);
    React.useEffect(function () {
        setIsOpen(open);
        var handleEscapeKey = function (e) {
            if (e.key === "Escape") {
                setIsOpen(false);
                onClose && onClose(e);
            }
        };
        if (open)
            document.addEventListener("keydown", handleEscapeKey);
        return function () {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [open, onClose]);
    var colorPalette = useTheme().theme.colorPalette;
    var _onOutsideClick = function (e) {
        if (hideOnOutsideClick) {
            setIsOpen(false);
            onClose && onClose(e);
        }
    };
    var Title = title && typeof title === "string" ? (_jsx(Text, __assign({ style: { flexGrow: 1 }, variant: "h5" }, { children: title }))) : (title);
    var CloseIcon = (_jsx("span", __assign({ style: { float: "right" } }, { children: _jsx(IconButton, { size: "small", variant: "outlined", icon: _jsx(IconX, {}), onClick: _onOutsideClick }) })));
    var headerDrawer = (title || hasCloseIcon) && (_jsxs(StyledHeader, __assign({ "$colorPalette": colorPalette }, { children: [_jsx("div", __assign({ style: { flexGrow: 1 } }, { children: Title })), CloseIcon] })));
    if (!open && !debouncedOpen)
        return null;
    return (_jsxs(_Fragment, { children: [_jsxs(StyledDrawer, __assign({ "$open": isOpen, "$colorPalette": colorPalette, "$position": position, "$size": size, className: className, style: style, "$zIndex": zIndex }, { children: [headerDrawer, _jsx("div", __assign({ style: { padding: "1rem" } }, { children: children }))] })), isOpen && (_jsx(StyledBackdrop, { "$backdropStrength": backdropStrength, onClick: _onOutsideClick }))] }));
};
export default Drawer;
var templateObject_1, templateObject_2, templateObject_3;
